"use client";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { ChildrenOf } from "@/Helpers/CenterOptions.types";
import { useT } from "@/Hooks";
import { TeamOutlined, UserOutlined } from "@ant-design/icons";
import { CheckCard } from "@ant-design/pro-components";
import { Button, Divider, Flex, Typography, theme } from "antd";
import { FC, useState } from "react";

const { Title, Text } = Typography;

export interface LandingAppointmentReservationConfirmationPageResult {
  Confirmed: boolean;
  ForMyself: boolean;
}

export interface LandingAppointmentReservationConfirmationPageProps {
  ServiceOnlinePaymentStatus?: ChildrenOf<"Status">;
  WorkTimeAlias: string;
  callback: (
    result: LandingAppointmentReservationConfirmationPageResult,
  ) => void;
}

const LandingAppointmentReservationConfirmationPage: FC<
  LandingAppointmentReservationConfirmationPageProps
> = ({ ServiceOnlinePaymentStatus, WorkTimeAlias, callback }) => {
  const t = useT();
  const { token } = theme.useToken();
  const [selected, setSelected] = useState<"self" | "family" | null>(null);

  const handleConfirm = () => {
    if (!selected) return;
    callback({ Confirmed: true, ForMyself: selected === "self" });
  };

  return (
    <div style={{ direction: "rtl", textAlign: "center" }}>
      <Title level={5} style={{ marginBottom: 4 }}>
        {t(
          "landing.appointment.reservationConfirmation.confirmAppointment",
          undefined,
          "Reservation confirmation",
        )}
      </Title>
      <Text type="secondary">
        {t(
          "landing.appointment.reservationConfirmation.timeAppointment",
          undefined,
          "Reservation confirmation",
        )}{" "}
        <b style={{ color: token.colorPrimary }}>{WorkTimeAlias}</b>{" "}
        {t(
          "landing.appointment.reservationConfirmation.areYouSure",
          undefined,
          "Reservation confirmation",
        )}
      </Text>

      <Divider />

      <Title level={5} style={{ marginBottom: 16 }}>
        {t(
          "landing.appointment.reservationConfirmation.appointment",
          undefined,
          "Reservation confirmation",
        )}
      </Title>

      <Flex justify="center" gap={16} wrap>
        <CheckCard
          title={
            <Flex
              vertical
              align="center"
              justify="center"
              style={{ height: 120, textAlign: "center" }}
              gap={8}
            >
              <TeamOutlined
                style={{ fontSize: 32, color: token.colorTextSecondary }}
              />
              <Text strong>
                {t(
                  "landing.appointment.reservationConfirmation.familyMember",
                  undefined,
                  "Reservation confirmation",
                )}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t(
                  "landing.appointment.reservationConfirmation.reserveForFamilyMember",
                  undefined,
                  "Reservation confirmation",
                )}
              </Text>
            </Flex>
          }
          checked={selected === "family"}
          onChange={(checked) => checked && setSelected("family")}
          style={{
            width: 180,
            height: 140,
            textAlign: "center",
            overflow: "hidden",
            borderColor:
              selected === "family"
                ? token.colorPrimary
                : token.colorBorderSecondary,
          }}
        />

        <CheckCard
          title={
            <Flex
              vertical
              align="center"
              justify="center"
              style={{ height: 120, textAlign: "center" }}
              gap={8}
            >
              <UserOutlined
                style={{ fontSize: 32, color: token.colorTextSecondary }}
              />
              <Text strong>
                {t(
                  "landing.appointment.reservationConfirmation.forMyself",
                  undefined,
                  "Reservation confirmation",
                )}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t(
                  "landing.appointment.reservationConfirmation.registeredForYou",
                  undefined,
                  "Reservation confirmation",
                )}
              </Text>
            </Flex>
          }
          checked={selected === "self"}
          onChange={(checked) => checked && setSelected("self")}
          style={{
            width: 180,
            height: 140,
            textAlign: "center",
            overflow: "hidden",
            borderColor:
              selected === "self"
                ? token.colorPrimary
                : token.colorBorderSecondary,
          }}
        />
      </Flex>

      <Divider style={{ margin: "16px 0" }} />

      <Flex justify="center" gap={8}>
        <Button
          onClick={() => callback({ Confirmed: false, ForMyself: false })}
          size="large"
          style={{ minWidth: 120 }}
        >
          {t("common.action.cancel", undefined, "Reservation confirmation")}
        </Button>
        <Button
          type="primary"
          disabled={!selected}
          onClick={handleConfirm}
          size="large"
          style={{ minWidth: 120 }}
        >
          {ServiceOnlinePaymentStatus == CenterOptions.Enum.Status.Active.key
            ? t(
              "landing.appointment.reservationConfirmation.reserveAndPay",
              undefined,
              "Reservation confirmation",
            )
            : t(
              "landing.appointment.reservationConfirmation.reserve",
              undefined,
              "Reservation confirmation",
            )}
        </Button>
      </Flex>
    </div>
  );
};

export default LandingAppointmentReservationConfirmationPage;
